// src/features/reits-ranking/components/FactorSummary.tsx
//
// Aggregate factor-strength panel for the REIT universe. The ETF/equity
// pages use a multi-spoke radar keyed by category/sector, but the REIT
// universe is a SINGLE "Real Estate" sector (the v1 screener proxy), so a
// radar would collapse to one spoke. Instead we surface the universe-wide
// average Momentum / Quality / Sentiment z-scores as diverging horizontal
// bars centred on zero — green to the right (factor "lit up" positive),
// red to the left (negative). Driven by GET /reits-ranking/sector-summary.

import type { SectorSummaryRow } from '../api';

interface FactorSummaryProps {
    sectors: SectorSummaryRow[];
}

interface FactorBar {
    label: string;
    value: number;
}

// z-scores are winsorised to +/- 4 by the scorer; we render the bar fill
// proportional to that bound so a +2 reads as half-width.
const Z_CLIP = 4;

const DivergingBar = ({ label, value }: FactorBar) => {
    const clamped = Math.max(-Z_CLIP, Math.min(Z_CLIP, value));
    // Half-width fraction of the (full-width) track, per side.
    const widthPct = (Math.abs(clamped) / Z_CLIP) * 50;
    const positive = clamped >= 0;
    const fillClass = positive
        ? 'bg-green-500 dark:bg-emerald-500'
        : 'bg-red-500 dark:bg-red-400';

    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">
                    {label}
                </span>
                <span
                    className={`font-mono font-semibold ${
                        positive
                            ? 'text-green-600 dark:text-emerald-400'
                            : 'text-red-600 dark:text-red-400'
                    }`}
                >
                    {value > 0 ? '+' : ''}
                    {value.toFixed(2)}
                </span>
            </div>
            {/* Track with a centre zero-line; the fill grows from the centre
                outward, left for negative and right for positive. */}
            <div className="relative h-2.5 w-full rounded-full bg-slate-100 dark:bg-slate-800">
                <div className="absolute left-1/2 top-0 h-full w-px bg-slate-300 dark:bg-slate-600" />
                <div
                    className={`absolute top-0 h-full rounded-full ${fillClass}`}
                    style={
                        positive
                            ? { left: '50%', width: `${widthPct}%` }
                            : { right: '50%', width: `${widthPct}%` }
                    }
                />
            </div>
        </div>
    );
};

export const FactorSummary = ({ sectors }: FactorSummaryProps) => {
    if (!sectors || sectors.length === 0) {
        return (
            <p className="muted text-sm">
                No factor aggregates available yet.
            </p>
        );
    }

    // Universe-wide aggregate: weight each sector row's averages by its
    // count so a multi-sector universe (future-proofing) collapses to a
    // single fair mean. With the v1 single-sector universe this is just
    // the lone row's values.
    const totalCount = sectors.reduce((acc, s) => acc + (s.count ?? 0), 0) || 1;
    const weighted = (pick: (s: SectorSummaryRow) => number): number =>
        sectors.reduce((acc, s) => acc + pick(s) * (s.count ?? 0), 0) /
        totalCount;

    const bars: FactorBar[] = [
        { label: 'Momentum', value: weighted((s) => s.avg_momentum_z) },
        { label: 'Quality', value: weighted((s) => s.avg_fundamental_z) },
        { label: 'Sentiment', value: weighted((s) => s.avg_sentiment_z) },
    ];

    return (
        <div className="space-y-5">
            <div className="space-y-4">
                {bars.map((b) => (
                    <DivergingBar key={b.label} label={b.label} value={b.value} />
                ))}
            </div>
            <p className="muted text-xs">
                Universe-wide average z-scores across {totalCount} REITs.
                A factor leaning right (green) is strong on average; left
                (red) is weak.
            </p>
        </div>
    );
};

export default FactorSummary;
